import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const testimonials = [
  {
    quote: "My son has autism and we tried three different programs before Creative Institute. Within two weeks of piano lessons, he was more focused and communicative than he'd been in years. This place changed our family.",
    name: 'Denise Marshall',
    role: 'Parent · Neurodivergent Development Program',
    rating: 5,
    img: 'https://public.readdy.ai/ai/img_res/70054a115b9c5f165d1888e13c84e46b.jpg',
    featured: true,
  },
  {
    quote: "I walked in not knowing anything about drums. After 4 sessions I was playing actual songs. The instructors here are patient, real, and they actually care.",
    name: 'Marcus Thompson',
    role: 'Student · On-Site Program',
    rating: 5,
    img: 'https://readdy.ai/api/search-image?query=portrait%20of%20a%20young%20Black%20man%20in%20his%20early%2020s%20with%20a%20confident%20warm%20smile%2C%20casual%20shirt%2C%20clean%20background%2C%20soft%20natural%20lighting%2C%20authentic%20photography&width=400&height=400&seq=testimonial-marcus-v1&orientation=squarish',
    featured: false,
  },
  {
    quote: "We booked a walk-in lesson for my daughter just to see if she'd like it. She cried when it was over because she didn't want to leave. We enrolled in the Standard Plan the same day.",
    name: 'Tamika Reeves',
    role: 'Parent · Standard Plan',
    rating: 5,
    img: 'https://readdy.ai/api/search-image?query=portrait%20of%20a%20Black%20woman%20in%20her%2040s%20with%20locs%2C%20warm%20smile%2C%20indoor%20soft%20lighting%2C%20professional%20but%20approachable%2C%20clean%20background%2C%20authentic%20photography&width=400&height=400&seq=testimonial-tamika-v1&orientation=squarish',
    featured: false,
  },
  {
    quote: "I hired Creative Institute for our church gala and the live band performance was phenomenal. Professional from start to finish — our guests are still talking about it.",
    name: 'Pastor David Ellis',
    role: 'Client · Off-Site Services',
    rating: 5,
    img: 'https://readdy.ai/api/search-image?query=portrait%20of%20a%20Black%20man%20in%20his%2050s%20with%20distinguished%20grey%20at%20temples%2C%20wearing%20a%20suit%2C%20warm%20confident%20smile%2C%20clean%20dark%20background%2C%20professional%20photography&width=400&height=400&seq=testimonial-pastor-v1&orientation=squarish',
    featured: false,
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <i key={i} className={`text-sm ${i < count ? 'ri-star-fill text-brand-yellow' : 'ri-star-line text-gray-300'}`} />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: featRef, isVisible: featVis } = useScrollAnimation({ threshold: 0.1 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const featured = testimonials.find(t => t.featured)!;
  const rest = testimonials.filter(t => !t.featured);

  return (
    <section className="py-28 bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#1E3A8A] relative overflow-hidden">
      {/* Decorative glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-brand-purple/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-brand-blue/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-8">

        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs font-bold uppercase tracking-widest bg-brand-yellow/10 mb-5`}>
            Real Stories. Real Impact.
          </span>
          <h2 className={`scroll-hidden delay-100 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-white mb-4 leading-tight`}>
            What Our Community<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow to-[#F59E0B]">Is Saying</span>
          </h2>
          <p className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-body text-white/55 text-base max-w-xl mx-auto leading-relaxed`}>
            From parents of neurodivergent students to first-time musicians and event clients — these are the people whose lives Creative Institute has touched.
          </p>
        </div>

        {/* Featured testimonial */}
        <div ref={featRef} className={`scroll-hidden ${featVis ? 'scroll-visible' : ''} mb-8`}>
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 border border-white/15 backdrop-blur-sm p-8 lg:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col lg:flex-row gap-10 items-center relative z-10">
              {/* Photo */}
              <div className="flex-shrink-0">
                <div className="relative w-28 h-28 lg:w-36 lg:h-36 rounded-full overflow-hidden border-4 border-brand-yellow/40">
                  {!imgErrors[featured.name] ? (
                    <img
                      src={featured.img}
                      alt={featured.name}
                      className="w-full h-full object-cover object-top"
                      onError={() => setImgErrors(p => ({ ...p, [featured.name]: true }))}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-brand-yellow/20">
                      <i className="ri-user-line text-3xl text-brand-yellow" />
                    </div>
                  )}
                </div>
              </div>
              {/* Quote */}
              <div className="flex-1">
                <StarRating count={featured.rating} />
                <div className="mt-4 mb-6">
                  <i className="ri-double-quotes-l text-4xl text-brand-yellow/30 block mb-2" />
                  <p className="font-heading text-white text-xl lg:text-2xl font-light leading-relaxed italic">
                    &ldquo;{featured.quote}&rdquo;
                  </p>
                </div>
                <div>
                  <p className="font-heading text-brand-yellow text-base font-bold">{featured.name}</p>
                  <p className="font-body text-white/50 text-sm mt-0.5">{featured.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rest of testimonials */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {rest.map((t, i) => (
            <div
              key={t.name}
              className={`scroll-hidden ${gridVis ? 'scroll-visible' : ''} rounded-2xl bg-white/8 border border-white/12 p-7 flex flex-col gap-4 card-lift hover:bg-white/12 transition-colors`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <StarRating count={t.rating} />
              <p className="font-body text-white/75 text-sm leading-relaxed flex-1 italic">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/20">
                  {!imgErrors[t.name] ? (
                    <img
                      src={t.img}
                      alt={t.name}
                      className="w-full h-full object-cover object-top"
                      onError={() => setImgErrors(p => ({ ...p, [t.name]: true }))}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-white/10">
                      <i className="ri-user-line text-sm text-white/60" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-heading text-white text-sm font-bold">{t.name}</p>
                  <p className="font-body text-white/45 text-xs">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`scroll-hidden delay-300 ${gridVis ? 'scroll-visible' : ''} text-center`}>
          <p className="font-body text-white/45 text-sm mb-5">
            Become our next success story.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/enroll"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-colors"
            >
              <i className="ri-arrow-right-circle-line text-lg" /> Enroll Now
            </Link>
            <Link
              to="/contact"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 text-white font-body font-semibold text-sm cursor-pointer hover:border-white/60 transition-colors"
            >
              <i className="ri-phone-line text-lg" /> Talk to Us First
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
